import React from 'react'
import type { MailLayout } from '../../types/communications'

interface Props {
  designs: MailLayout[]
  value: string
  disabled?: boolean
  onChange: (key: string) => void
}

// A miniature of the design, drawn with plain divs. Cheaper and steadier than
// rendering seven iframes, and enough to tell the layouts apart at a glance.
const Wireframe: React.FC<{ shape: MailLayout['wireframe'] }> = ({ shape }) => {
  const bar = 'rounded-full bg-gray-300'
  const band = 'rounded-t-md bg-gradient-to-r from-blue-500 to-blue-700'
  return <div className="flex h-24 flex-col overflow-hidden rounded-md border border-gray-200 bg-white">
    {shape === 'gradient' && <div className={`${band} flex h-11 flex-col items-center justify-center gap-1 px-3`}>
      <div className="h-1 w-8 rounded-full bg-white/70" />
      <div className="h-1.5 w-16 rounded-full bg-white" />
    </div>}
    {shape === 'banner' && <div className="flex h-10 flex-col justify-center gap-1 bg-blue-600 px-3">
      <div className="h-1 w-8 rounded-full bg-white/70" />
      <div className="h-1.5 w-14 rounded-full bg-white" />
    </div>}
    {shape === 'stripe' && <div className="h-1.5 bg-amber-500" />}
    {(shape === 'header' || shape === 'sober' || shape === 'editorial' || shape === 'stripe') &&
      <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
        <div className={`${bar} h-1.5 w-7`} />
        {shape === 'sober' && <div className={`${bar} h-1 w-4`} />}
      </div>}
    {shape === 'centered' && <div className="flex flex-col items-center gap-1.5 px-3 py-2">
      <div className={`${bar} h-1.5 w-7`} />
      <div className="h-6 w-20 rounded border border-dashed border-gray-300 bg-gray-50" />
    </div>}
    <div className={`flex flex-1 flex-col gap-1.5 px-3 py-2 ${shape === 'centered' ? 'items-center' : ''}`}>
      {shape === 'editorial' && <div className={`${bar} mb-0.5 h-2 w-24`} />}
      <div className={`${bar} h-1 w-full`} />
      <div className={`${bar} h-1 w-5/6`} />
      {shape !== 'bare' && <div className={`${bar} h-1 w-4/6`} />}
      <div className="mt-auto h-3 w-14 rounded bg-blue-600" />
    </div>
  </div>
}

const CampaignDesignPicker: React.FC<Props> = ({ designs, value, disabled, onChange }) =>
  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
    {designs.map(design => <button key={design.key} type="button" disabled={disabled}
      onClick={() => onChange(design.key)}
      className={`rounded-xl border p-2 text-left transition disabled:opacity-60 ${
        value === design.key
          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
          : 'border-gray-200 bg-white hover:border-blue-300'
      }`}>
      <Wireframe shape={design.wireframe} />
      <div className="mt-2 text-[11px] font-semibold text-gray-800">{design.name}</div>
      <div className="mt-0.5 line-clamp-2 text-[10px] leading-4 text-gray-500">{design.description}</div>
    </button>)}
  </div>

export default CampaignDesignPicker
